import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Check, CheckCircle2, Timer } from "lucide-react";
import { DashboardMockup } from "./DashboardMockup";
import { EASE } from "./Reveal";
import { CTA_URL } from "../lib/constants";
import { handleAnchor } from "../lib/scroll";

const LINES = [
    { text: "Build Websites & Apps", gradient: false },
    { text: "with AI —", gradient: true },
    { text: "no coding required.", gradient: false },
];

const TRUST = ["Free plan", "No credit card", "Live in minutes"];

const chipClass =
    "absolute z-10 hidden items-center gap-3 rounded-xl border border-white/10 bg-[#0D0D0D]/90 px-4 py-3 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur lg:flex";

export const Hero = () => {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const orbY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const mockY = useTransform(scrollYProgress, [0, 1], [0, -50]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(my, { stiffness: 120, damping: 18 });
    const rotateY = useSpring(mx, { stiffness: 120, damping: 18 });

    const handleMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 8);
        my.set(-((e.clientY - r.top) / r.height - 0.5) * 6);
    };
    const handleLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section ref={sectionRef} data-testid="hero-section" className="relative overflow-hidden px-6 pb-24 pt-32 sm:pt-36 lg:pt-40">
            <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_75%_65%_at_50%_0%,black,transparent)]" aria-hidden="true" />
            <motion.div
                className="float-slow absolute -top-32 left-1/4 h-[420px] w-[680px] rounded-full bg-[#8B5CF6]/20 blur-[130px]"
                style={{ y: orbY }}
                aria-hidden="true"
            />
            <div className="absolute bottom-0 right-[-100px] h-[320px] w-[320px] rounded-full bg-[#00C2FF]/10 blur-[110px]" aria-hidden="true" />

            <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2 lg:gap-10">
                <div className="text-center lg:text-left">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur"
                    >
                        <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#00C2FF]" />
                        <span className="text-xs font-medium tracking-wide text-white/70 sm:text-sm">
                            Powered by Emergent — the AI app builder
                        </span>
                    </motion.div>

                    <h1 className="font-display text-4xl font-medium leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${
                                        line.gradient
                                            ? "bg-gradient-to-r from-[#00C2FF] to-[#8B5CF6] bg-clip-text pb-2 text-transparent"
                                            : "text-white"
                                    }`}
                                    initial={{ y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.95, delay: 0.15 + i * 0.14, ease: EASE }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                        className="mx-auto mt-6 max-w-xl text-base text-white/70 sm:text-lg lg:mx-0"
                    >
                        Emergent turns your ideas into fully functional websites, web apps, and business tools in
                        minutes — just describe what you want.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                        className="mt-9 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start"
                    >
                        <a
                            href={CTA_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-primary"
                            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-medium text-black transition-transform duration-200 hover:scale-105 sm:w-auto"
                        >
                            Start Building Free
                            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#how-it-works"
                            data-testid="hero-cta-secondary"
                            onClick={handleAnchor("#how-it-works")}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-white/80 transition-colors duration-200 hover:bg-white/5 hover:text-white sm:w-auto"
                        >
                            <Play size={16} />
                            See How It Works
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                        className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 lg:justify-start"
                    >
                        {TRUST.map((item) => (
                            <li key={item} className="flex items-center gap-1.5 text-sm text-white/50">
                                <Check size={14} className="text-[#00C2FF]" />
                                {item}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                    className="relative"
                    style={{ y: mockY }}
                >
                    <div className="absolute -inset-8 -z-10 rounded-[40px] bg-[#8B5CF6]/15 blur-[80px]" aria-hidden="true" />
                    <div className="[perspective:1400px]" onMouseMove={handleMove} onMouseLeave={handleLeave}>
                        <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
                            <DashboardMockup />
                        </motion.div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 1.4, ease: EASE }}
                        className={`${chipClass} float-slow -left-8 top-8`}
                    >
                        <CheckCircle2 size={18} strokeWidth={1.5} className="text-[#00C2FF]" />
                        <div>
                            <p className="text-xs font-medium text-white">Deployed</p>
                            <p className="font-mono text-[10px] text-white/40">yourapp.emergent.app</p>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 1.6, ease: EASE }}
                        className={`${chipClass} -right-6 bottom-10`}
                        style={{ animationDelay: "1.2s" }}
                    >
                        <Timer size={18} strokeWidth={1.5} className="text-[#8B5CF6]" />
                        <div>
                            <p className="text-xs font-medium text-white">Build time</p>
                            <p className="font-mono text-[10px] text-white/40">3m 42s</p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
